import React from 'react';
import { getRunStatus } from '../../../utilityFunctions/dashboardApi';
import { RUN_POLL_MS } from './RunProgressPanel';

const TERMINAL = ['COMPLETED', 'FAILED', 'AWAITING_HITL'];

/**
 * Polls GET /runs/{runId}/pipeline-status every RUN_POLL_MS (same cadence and stop rule as
 * RunProgressPanel) for runs that render inline instead of in the panel, e.g. a widget repair.
 * Polling stops at COMPLETED / FAILED / AWAITING_HITL and calls onTerminal(status) once per stop;
 * restart() resumes it after a HITL checkpoint was answered.
 *
 * @returns {{ status: object|null, restart: function }}
 */
export default function useRunPolling(runId, jwt, onTerminal) {
  const [status, setStatus] = React.useState(null);
  const [epoch, setEpoch] = React.useState(0);
  const cbRef = React.useRef(onTerminal);
  cbRef.current = onTerminal;

  React.useEffect(() => { setStatus(null); }, [runId]);

  React.useEffect(() => {
    if (!runId) return undefined;
    let alive = true;
    let fired = false;
    let intervalId = null;
    const stop = () => { if (intervalId) clearInterval(intervalId); intervalId = null; };
    const pollOnce = async () => {
      const res = await getRunStatus(runId, jwt);
      if (!alive || fired || !res.ok || !res.data) return;
      setStatus(res.data);
      if (TERMINAL.includes(res.data.status)) {
        fired = true;
        stop();
        if (cbRef.current) cbRef.current(res.data);
      }
    };
    intervalId = setInterval(pollOnce, RUN_POLL_MS);
    pollOnce();
    return () => { alive = false; stop(); };
  }, [runId, jwt, epoch]);

  const restart = React.useCallback(() => {
    setStatus((s) => (s ? { ...s, status: 'RUNNING', checkpoint: null } : s));
    setEpoch((e) => e + 1);
  }, []);

  return { status, restart };
}
